import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { timeAgo } from "@/lib/utils-ros";
import { Ticket as TicketIcon, AlertCircle, CheckCircle2, RefreshCcw, Loader2, Plus, Monitor, Filter, X } from "lucide-react";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { NewTicketModal } from "@/components/revenueos/NewTicketModal";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const PRIORITY_COLOR = { critical: "#EF4444", high: "var(--copper)", medium: "#F59E0B", low: "#3B82F6" };
const STATUS_COLOR = { open: "#EF4444", triaged: "#F59E0B", in_progress: "var(--copper)", resolved: "#10B981", closed: "var(--text-dim)" };

function ticketLabel(value) {
  return String(value || "").replaceAll("_", " ").replace(/\b\w/g, char => char.toUpperCase());
}

function TicketFilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-[#455A64] mb-1.5">{label}</span>
      <select value={value} onChange={event => onChange(event.target.value)} data-testid={testId} className="w-full h-10 rounded-xl border border-[#B8D3DF] bg-[#F8FCFE] px-3 text-[12px] outline-none focus:border-[#00A2D4]">
        {options.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
      </select>
    </label>
  );
}

export default function Tickets() {
  const { canWrite } = useAuth();
  const canManageTickets = canWrite("customers");
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/tickets");
      setTickets(r.data);
    } catch {
      toast.error("Could not load tickets");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      setQuery(q);
      setFilter("all");
    }
  }, [searchParams]);

  async function setStatus(tid, status) {
    setBusy(tid);
    try {
      await api.patch(`/tickets/${tid}`, { status });
      toast.success(status === "open" ? "Re-opened" : status === "resolved" ? "Resolved" : status === "closed" ? "Closed" : `Status: ${status}`);
      await load();
    } catch { toast.error("Update failed"); }
    finally { setBusy(null); }
  }

  const shown = tickets.filter(t => {
    if (filter === "all") { /* pass */ }
    else if (filter === "open") { if (!["open", "triaged", "in_progress"].includes(t.status)) return false; }
    else { if (t.status !== filter) return false; }
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [t.ticket_no, t.subject, t.company?.name, t.category, t.priority, t.status].filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  const openCount = tickets.filter(t => ["open", "triaged", "in_progress"].includes(t.status)).length;
  const resolvedCount = tickets.filter(t => t.status === "resolved").length;
  const closedCount = tickets.filter(t => t.status === "closed").length;
  const categories = [...new Set(tickets.map(ticket => ticket.category).filter(Boolean))].sort();
  const activeFilterCount = [filter !== "all", priorityFilter !== "all", categoryFilter !== "all"].filter(Boolean).length;

  function clearFilters() {
    setFilter("all");
    setPriorityFilter("all");
    setCategoryFilter("all");
  }

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="tickets-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-6 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">Tickets</h1>
            <p className="text-[13px] text-white/75 mt-1">Customer issues logged, prioritised, and resolved from one support queue.</p>
          </div>
          {canManageTickets && (
          <button
            onClick={() => setNewOpen(true)}
            data-testid="new-ticket-btn"
            className="h-10 px-4 rounded-xl bg-[#E58D22] hover:bg-[#C97715] !text-white text-[12px] font-semibold flex items-center gap-2 shrink-0 shadow-[0_7px_18px_rgba(229,141,34,0.28)]"
          >
            <Plus size={14} /> Open ticket
          </button>
          )}
        </div>
      </section>

      <NewTicketModal open={newOpen} onOpenChange={setNewOpen} onCreated={() => load()} />

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        <PageToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search tickets by number, subject, customer, or category…"
          entityLabel="tickets"
          count={shown.length}
          variant="universe"
          right={(
            <div className="relative ml-auto">
              <button
                type="button"
                onClick={() => setFilterOpen(open => !open)}
                data-testid="tickets-filter-btn"
                className={`h-9 px-3 rounded-lg border text-[12px] font-medium flex items-center gap-2 shadow-[0_4px_12px_rgba(7,83,154,0.06)] transition-colors ${filterOpen || activeFilterCount ? "border-[#07539A] bg-[#E3F2F8] text-[#07539A]" : "border-[#BFD6E1] bg-white text-[#243B53] hover:border-[#07539A]"}`}
              >
                <Filter size={14} /> Filter
                {activeFilterCount > 0 && <span className="w-5 h-5 rounded-full bg-[#07539A] !text-white text-[10px] flex items-center justify-center">{activeFilterCount}</span>}
              </button>
              {filterOpen && (
                <div className="absolute right-0 top-11 z-30 w-[min(92vw,34rem)] rounded-2xl border border-[#C9DCE6] bg-white shadow-[0_20px_50px_rgba(7,83,154,0.20)] p-4" data-testid="tickets-filter-panel">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="font-display text-lg text-[#07539A]">Filter tickets</div>
                      <div className="text-[10px] text-[#78909C] mt-0.5">Focus the support queue by status, urgency, and issue type.</div>
                    </div>
                    <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters" className="w-8 h-8 rounded-full hover:bg-[#EDF7FB] text-[#607D8B] flex items-center justify-center"><X size={14} /></button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <TicketFilterSelect label="Status" value={filter} onChange={setFilter} testId="tickets-status-filter" options={[
                      { key: "all", label: `All tickets (${tickets.length})` },
                      { key: "open", label: `Open queue (${openCount})` },
                      { key: "resolved", label: `Resolved (${resolvedCount})` },
                      { key: "closed", label: `Closed (${closedCount})` },
                    ]} />
                    <TicketFilterSelect label="Priority" value={priorityFilter} onChange={setPriorityFilter} testId="tickets-priority-filter" options={[
                      { key: "all", label: "All priorities" },
                      ...["critical", "high", "medium", "low"].map(value => ({ key: value, label: ticketLabel(value) })),
                    ]} />
                    <TicketFilterSelect label="Category" value={categoryFilter} onChange={setCategoryFilter} testId="tickets-category-filter" options={[
                      { key: "all", label: "All categories" },
                      ...categories.map(value => ({ key: value, label: ticketLabel(value) })),
                    ]} />
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#E1EBF0] flex items-center justify-between">
                    <button type="button" onClick={clearFilters} className="text-[11px] text-[#607D8B] hover:text-[#07539A]">Clear all</button>
                    <button type="button" onClick={() => setFilterOpen(false)} className="h-9 px-4 rounded-xl bg-[#07539A] !text-white text-[12px] font-medium">Show {shown.length} tickets</button>
                  </div>
                </div>
              )}
            </div>
          )}
        />

      {loading ? <div className="bg-white border border-[#C9DCE6] rounded-[22px] text-[#78909C] p-10 text-center shadow-[0_10px_28px_rgba(7,83,154,0.08)]">Loading tickets…</div> :
       shown.length === 0 ? (
         <div className="text-center p-10 border border-dashed border-[#B8D3DF] bg-white rounded-[22px]">
           <span className="w-11 h-11 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mx-auto mb-3"><TicketIcon size={21} /></span>
           <div className="text-[13px] text-[#78909C]">No tickets in this bucket.</div>
         </div>
       ) : (
        <div className="border border-[#C9DCE6] rounded-[22px] overflow-x-auto bg-white shadow-[0_10px_28px_rgba(7,83,154,0.08)]" data-testid="tickets-table-scroll">
          <div className="grid min-w-[980px] grid-cols-[90px_minmax(220px,1.7fr)_105px_75px_85px_100px_190px] gap-3 px-4 py-3 bg-[#F3FAFD] border-b border-[#D7E8F0] text-[10px] font-semibold text-[#07539A]">
            <div>Ticket</div>
            <div>Subject &amp; customer</div>
            <div>Category</div>
            <div className="text-center">Priority</div>
            <div className="text-center">Status</div>
            <div className="text-center">Opened</div>
            <div className="text-center">Actions</div>
          </div>
          <div className="min-w-[980px] divide-y divide-[#E1EBF0]">
          {shown.map(t => {
            const isOpen = ["open", "triaged", "in_progress"].includes(t.status);
            const isResolved = t.status === "resolved";
            const isClosed = t.status === "closed";
            const fromErgoniq = t.ingest_source === "ergoniq";
            const meta = t.ingest_meta || {};
            return (
              <div key={t.id} data-testid={`ticket-${t.id}`} className="relative px-4 py-4 hover:bg-[#F8FCFE] transition-colors">
                <span className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full" style={{ background: PRIORITY_COLOR[t.priority] }} />
                <div className="grid grid-cols-[90px_minmax(220px,1.7fr)_105px_75px_85px_100px_190px] gap-3 items-center">
                  <div className="text-[11px] font-medium text-[#607D8B] truncate">{t.ticket_no}</div>
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium text-[#455A64] truncate">{t.subject}</div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[10px] text-[#78909C]">
                      <span>{t.company?.name}</span>
                      {t.partner?.id && <span>· Partner: {t.partner.firm || t.partner.name}</span>}
                      {!t.partner?.id && <span className="text-[#B26E1F]">· Awaiting partner assignment</span>}
                      {fromErgoniq && (
                        <span className="flex items-center gap-1 text-[9px] text-[#C76500]">
                          <Monitor size={9} /> Ergoniq
                        </span>
                      )}
                      {t.sla_breach && (
                        <span className="flex items-center gap-1 text-[9px] text-[#DC2626]">
                          <AlertCircle size={9} /> SLA breach
                        </span>
                      )}
                      {fromErgoniq && meta.module && <span>· {meta.module}</span>}
                      {fromErgoniq && meta.reporter_name && <span>· {meta.reporter_name}</span>}
                    </div>
                  </div>
                  <div className="text-[11px] text-[#607D8B] capitalize">{t.category.replace("_", " ")}</div>
                  <div className="text-center text-[10px] font-semibold capitalize" style={{ color: PRIORITY_COLOR[t.priority] }}>{t.priority}</div>
                  <div className="text-center">
                    <span className="text-[9px] font-semibold px-2.5 py-1 rounded-full capitalize inline-block" style={{
                      background: `${STATUS_COLOR[t.status]}15`, color: STATUS_COLOR[t.status], border: `1px solid ${STATUS_COLOR[t.status]}30`
                    }}>{t.status.replace("_", " ")}</span>
                  </div>
                  <div className="text-center text-[10px] text-[#78909C]">{timeAgo(t.created_at)}</div>
                  <div className="flex justify-center flex-nowrap gap-1.5" data-testid={`ticket-actions-${t.id}`}>
                  {canManageTickets && isOpen && (
                    <>
                      <button
                        onClick={() => setStatus(t.id, "resolved")}
                        disabled={busy === t.id}
                        data-testid={`ticket-resolve-${t.id}`}
                        className="h-8 px-2.5 rounded-lg bg-[#159A68] hover:bg-[#0D8055] !text-white text-[10px] font-medium whitespace-nowrap flex items-center gap-1 disabled:opacity-50"
                      >
                        {busy === t.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                        Resolve
                      </button>
                      <button
                        onClick={() => setStatus(t.id, "closed")}
                        disabled={busy === t.id}
                        data-testid={`ticket-close-${t.id}`}
                        className="h-8 px-2.5 rounded-lg border border-[#B8D3DF] bg-white hover:border-[#07539A] hover:text-[#07539A] text-[10px] font-medium whitespace-nowrap flex items-center gap-1 disabled:opacity-50 text-[#607D8B]"
                      >
                        Close ticket
                      </button>
                    </>
                  )}
                  {canManageTickets && isResolved && (
                    <>
                      <button
                        onClick={() => setStatus(t.id, "closed")}
                        disabled={busy === t.id}
                        data-testid={`ticket-close-${t.id}`}
                        className="h-8 px-2.5 rounded-lg bg-[#07539A] hover:bg-[#063F73] !text-white text-[10px] font-medium whitespace-nowrap flex items-center gap-1 disabled:opacity-50"
                      >
                        Close ticket
                      </button>
                      <button
                        onClick={() => setStatus(t.id, "open")}
                        disabled={busy === t.id}
                        data-testid={`ticket-reopen-${t.id}`}
                        className="h-8 px-2.5 rounded-lg border border-[#B8D3DF] bg-white hover:border-[#E58D22] hover:text-[#C76500] text-[10px] font-medium whitespace-nowrap flex items-center gap-1 disabled:opacity-50 text-[#607D8B]"
                      >
                        <RefreshCcw size={11} /> Re-open
                      </button>
                    </>
                  )}
                  {canManageTickets && isClosed && (
                    <button
                      onClick={() => setStatus(t.id, "open")}
                      disabled={busy === t.id}
                      data-testid={`ticket-reopen-${t.id}`}
                      className="h-8 px-2.5 rounded-lg border border-[#B8D3DF] bg-white hover:border-[#E58D22] hover:text-[#C76500] text-[10px] font-medium whitespace-nowrap flex items-center gap-1 disabled:opacity-50 text-[#607D8B]"
                    >
                      <RefreshCcw size={11} /> Re-open
                    </button>
                  )}
                  </div>
                </div>

              </div>
            );
          })}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
